<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs" />

    <div class="row g-3">
      <div class="col-lg-3">
        <div class="card">
          <div class="card-body py-2">
            <ul class="nav nav-pills flex-column">
              <li class="nav-item">
                <router-link to="/app/settings/profile" class="nav-link" active-class="active">
                  <vue-feather type="user" size="16" class="me-2" />
                  Profile
                </router-link>
              </li>
              <li class="nav-item">
                <router-link to="/app/settings/security" class="nav-link" active-class="active">
                  <vue-feather type="shield" size="16" class="me-2" />
                  Security
                </router-link>
              </li>
              <li class="nav-item" v-if="isSuperAdmin">
                <router-link to="/app/settings/app" class="nav-link" active-class="active">
                  <vue-feather type="settings" size="16" class="me-2" />
                  App Settings
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="col-lg-9">
        <div class="card">
          <div class="card-header">
            <h6 class="card-title mb-0">Profile Settings</h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <div class="row mb-3">
                <div class="col-md-3 text-center">
                  <div class="avatar-upload">
                    <img
                      :src="avatarPreview || user?.avatar || defaultAvatar"
                      class="rounded-circle mb-2"
                      width="100"
                      height="100"
                    />
                    <div>
                      <label class="btn btn-sm btn-outline-primary">
                        Change Photo
                        <input type="file" class="d-none" @change="handleAvatarChange" accept="image/*" />
                      </label>
                    </div>
                  </div>
                </div>
                <div class="col-md-9">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Full Name</label>
                      <input v-model="form.name" type="text" class="form-control" required />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Email</label>
                      <input v-model="form.email" type="email" class="form-control" required />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Phone</label>
                      <input v-model="form.phone" type="tel" class="form-control" />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Role</label>
                      <input :value="formatRole(user?.role)" type="text" class="form-control" disabled />
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>
              <div v-if="success" class="alert alert-success">Profile updated successfully!</div>

              <button type="submit" class="btn btn-primary btn-sm" :disabled="loading">
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                Save Changes
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import api from '@/services/api'

export default {
  name: 'ProfileSettings',
  setup() {
    const authStore = useAuthStore()
    const user = computed(() => authStore.user)
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Settings', active: true }
    ]

    const form = ref({ name: '', email: '', phone: '' })
    const avatarFile = ref(null)
    const avatarPreview = ref(null)
    const loading = ref(false)
    const error = ref('')
    const success = ref(false)
    const defaultAvatar = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="50" fill="%233d5ee1"/><text x="50" y="60" text-anchor="middle" fill="white" font-size="40">U</text></svg>'

    onMounted(() => {
      if (user.value) {
        form.value = {
          name: user.value.name,
          email: user.value.email,
          phone: user.value.phone || ''
        }
      }
    })

    const handleAvatarChange = (e) => {
      const file = e.target.files[0]
      if (file) {
        avatarFile.value = file
        avatarPreview.value = URL.createObjectURL(file)
      }
    }

    const formatRole = (role) => {
      if (!role) return ''
      return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''
      success.value = false

      const formData = new FormData()
      formData.append('name', form.value.name)
      formData.append('email', form.value.email)
      formData.append('phone', form.value.phone)
      if (avatarFile.value) {
        formData.append('avatar', avatarFile.value)
      }

      try {
        const response = await api.put('/v1/admin/settings/profile', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        authStore.user = response.data.user
        success.value = true
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update profile'
      } finally {
        loading.value = false
      }
    }

    return {
      breadcrumbs,
      user,
      isSuperAdmin,
      form,
      avatarPreview,
      loading,
      error,
      success,
      defaultAvatar,
      handleAvatarChange,
      formatRole,
      handleSubmit
    }
  }
}
</script>
